import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import { SourceRefs } from './kit';
import type { AlternativePageProps } from './types';
import { delay, Stage } from '../primitives';
import { useReveal } from '../useReveal';

/** The honest part: when the other product is the right one. */
export default function BetterChoice({
  name,
  slug,
  betterChoice,
}: Pick<AlternativePageProps, 'name' | 'slug' | 'betterChoice'>) {
  const { ref, state } = useReveal<HTMLDivElement>(0.2);
  // Drift is being retired, so the question there is what to use instead.
  const retiring = slug === 'drift';

  return (
    <section id='better-choice' className='py-12 sm:py-16'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='ink'
          halftoneAt='100% 0%'
          className='px-6 py-14 sm:px-14 sm:py-20'
        >
          <div
            ref={ref}
            data-reveal={state}
            className={clsxm(
              styles.reveal,
              'grid gap-12 lg:grid-cols-12 lg:gap-16',
            )}
          >
            <div className='lg:col-span-5'>
              <p className='text-sm font-semibold uppercase tracking-[0.12em] text-primary-300'>
                In fairness
              </p>
              <h2
                className={clsxm(
                  styles.display,
                  'mt-4 text-[2.125rem] font-semibold leading-[1.08] text-white sm:text-[2.75rem]',
                )}
              >
                {retiring
                  ? 'When another tool is the better choice.'
                  : `When ${name} is the better choice.`}
              </h2>
              <p className='mt-6 max-w-md text-lg leading-8 text-[#b7b7d2]'>
                {retiring
                  ? 'Easy-Peasy.AI replaces Drift’s AI chat, not everything Drift did. Look elsewhere if this is you:'
                  : `We’d rather you pick the right tool than switch and regret it. ${name} is the stronger choice if:`}
              </p>
            </div>
            <ul className='space-y-4 lg:col-span-7'>
              {betterChoice.map((item, index) => (
                <li
                  key={item.text}
                  className={clsxm(
                    styles.rv,
                    'flex gap-4 rounded-2xl bg-white/[0.05] p-5 ring-1 ring-inset ring-white/10 sm:p-6',
                  )}
                  style={delay(index * 90)}
                >
                  <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/10 text-sm font-semibold text-white'>
                    {index + 1}
                  </span>
                  <p className='text-[16px] leading-7 text-[#d6d6ea]'>
                    {item.text}
                    <SourceRefs ids={item.sources} invert />
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </Stage>
      </div>
    </section>
  );
}
